<template>
  <pu-campsite-card
    v-bind="$props"
    :class="{
      'bookable-campsite': status === 'bookable',
      'basic-campsite': status !== 'bookable',
      'with-pitchtypes': pitchtypes,
      'many-pitchtypes': pitchtypes && pitchtypes.length > 2,
      'no-photo': !primaryPhoto,
      desktop: $isDesktop,
    }"
    @mouseover.once="userInteractedWithCard = true"
  >
    <template #image="{ searchResultIndex }">
      <pu-campsite-photos-provider
        :search-result-index="searchResultIndex"
        :primary-photo="primaryPhoto"
        :campsite-id="id"
        :category-ids="searchFilters ? searchFilters.categoryIds : []"
        :show-search-gallery="true"
        :load-gallery="userInteractedWithCard"
        :is-search-results="true"
      />
    </template>

    <template v-if="shouldShowOpenDate" #next-open>
      <translate
        v-if="isDateInSameYear(nextOpen)"
        class="next-open"
        :tt="{ nextOpenDate: nextOpen }"
      >
        Opens {nextOpenDateNoYear}
      </translate>
      <translate v-else class="next-open" :tt="{ nextOpenDate: nextOpen }">
        Opens {nextOpenDateYear}
      </translate>
    </template>

    <template #footer>
      <footer>
        <template v-if="isDatedSearch">
          <AsyncPuPricingResults
            :key="getAsyncComponentKey('pricingResults')"
            :pitchtypes="pitchtypes"
            :search-result-index="searchResultIndex"
            :number-of-pitchtypes-to-show="numberOfPitchtypesToShow"
            @vue:mounted="setAsyncComponentKey('pricingResults')"
          />
        </template>
        <template v-else>
          <div
            v-if="!availableToSearch && status === 'bookable'"
            class="zero-availability"
          >
            <div class="pitch-category-icons">
              <pu-category-icons :categories="categories" />
            </div>
            <translate>No availability at present on Pitchup.com</translate>
          </div>
          <template v-else>
            <input
              :id="pitchtypesSectionId"
              v-model="showPitchesExpanded"
              class="show-checkbox"
              type="checkbox"
              aria-hidden="true"
              tabindex="-1"
            />
            <ul v-if="pitchtypes && pitchtypes.length" class="pt-list">
              <li
                v-for="(pitchtype, index) in (pitchtypes || []).slice(
                  0,
                  numberOfPitchtypesToShow,
                )"
                :key="`pt3-${index}-${pitchtype.id}`"
              >
                <div class="pitch-info">
                  <div class="category-info">
                    <pu-category-icons :categories="pitchtype.categories" />
                  </div>
                  <span class="pitch-name">{{ pitchtype.name }}</span>
                </div>
                <lazy-pricing-period-non-dated
                  v-if="pitchtype.leadPrice"
                  :lead-price="pitchtype.leadPrice"
                  :lead-price-with-excluded="pitchtype.leadPriceWithExcluded"
                  :lead-price-nights="pitchtype.leadPriceNights"
                  :lead-price-persons="
                    'capacity' in pitchtype
                      ? Math.min(pitchtype.capacity, 2)
                      : 0
                  "
                />
              </li>
            </ul>

            <template
              v-if="pitchtypes && pitchtypes.length > numberOfPitchtypesToShow"
            >
              <ul class="pt-list additional-pitchtypes">
                <li
                  v-for="(pitchtype, index) in (pitchtypes || []).slice(
                    numberOfPitchtypesToShow,
                  )"
                  :key="`pt4-${index}-${pitchtype.id}`"
                >
                  <div class="pitch-info">
                    <div class="category-info">
                      <pu-category-icons :categories="pitchtype.categories" />
                    </div>
                    <span class="pitch-name">{{ pitchtype.name }}</span>
                  </div>

                  <lazy-pricing-period-non-dated
                    v-if="pitchtype.leadPrice"
                    :lead-price="pitchtype.leadPrice"
                    :lead-price-nights="pitchtype.leadPriceNights"
                    :lead-price-persons="
                      'capacity' in pitchtype
                        ? Math.min(pitchtype.capacity, 2)
                        : 0
                    "
                  />
                </li>
              </ul>
              <label
                :for="pitchtypesSectionId"
                :aria-label="$t('See more...')"
                class="show-more-pitches"
                @click.prevent="showMorePitches"
              >
                <span class="caret"
                  ><span class="chevron-l" /><span class="chevron-r" /></span
                ><translate class="subsection-close-txt">Close</translate
                ><slot name="subsection-open-txt"
                  ><translate
                    class="subsection-open-txt"
                    :data-sctrack="`${String($route.name)}--PuCampsitePriceCard-see-more`"
                    >See more...</translate
                  ></slot
                >
              </label>
            </template>
          </template>
        </template>
      </footer>
    </template>
    <template #notes>
      <slot name="notes" />
    </template>
  </pu-campsite-card>
</template>

<script lang="ts">
import { defineAsyncComponent } from 'vue'
import { hydrateOnVisibleSafe } from '../../../utility/hydration/hydrateOnVisibleSafe'
import { PitchType } from '~/apps/campsite-details/types'
import { SearchFilters } from '~/apps/search/schemas'
import { PitchTypePricingResult } from '~/apps/search/types'
import { isDateInSameYear, isDatePast } from '~/utility/date/relative'
import { SimpleMoney } from '~/utility/money'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import BaseCampsiteCard from './BaseCampsiteCard.vue'

const AsyncPuPricingResults = defineAsyncComponent({
  loader: () => import('./components/PuPricingResults.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

@Component({
  mixins: [BaseCampsiteCard],
  components: { AsyncPuPricingResults },
})
class PuCampsitePriceCard extends Vue {
  @Prop()
  pitchtypes?: PitchTypePricingResult[] | PitchType[]

  @Prop()
  nextOpen: string

  @Prop()
  leadPrice: SimpleMoney

  @Prop()
  leadPriceNights: number

  @Prop({ required: false })
  searchFilters?: SearchFilters

  @Prop({ required: false })
  searchResultIndex: number

  numberOfPitchtypesToShow = 4

  showPitchesExpanded = false

  userInteractedWithCard = false

  asyncComponentKeys: Record<string, string> = {}

  // Helper methods for async component keys
  getAsyncComponentKey(componentName: string): string {
    return (
      this.asyncComponentKeys[componentName] ||
      `${this.$route.fullPath.split('#')[0]}-${componentName}`
    )
  }

  setAsyncComponentKey(componentName: string): void {
    this.asyncComponentKeys[componentName] =
      `${this.$route.fullPath.split('#')[0]}-${componentName}`
  }

  get shouldShowOpenDate() {
    return this.nextOpen && !isDatePast(this.nextOpen)
  }

  isDateInSameYear(date: string) {
    return isDateInSameYear(date)
  }

  get pitchtypesSectionId() {
    return `pitchtypes${this.searchResultIndex}`
  }

  get isDatedSearch() {
    return this.searchFilters && this.searchFilters.dates
  }

  showMorePitches() {
    this.showPitchesExpanded = !this.showPitchesExpanded
  }
}

export default toNative(PuCampsitePriceCard)
</script>

<style scoped>
@import url('@/assets/css/search-cards-pitchtypes.css');

.next-open {
  font-size: var(--body);
  color: var(--colour-orange);
  position: relative;
  top: -0.35em;
}

.bookable-campsite :deep(.campsite-rating) {
  position: absolute;
  top: -0.3em;
  right: 1em;
}

.basic-campsite {
  min-height: 9em;
}

.basic-campsite :deep(.new-campsite) {
  display: none;
}

.basic-campsite :deep(.card-contents) {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: 4em;
}

.basic-campsite :deep(.rating-bubble) {
  position: absolute;
  top: -0.3em;
  right: 1em;
}

.basic-campsite.no-photo :deep(.rating-bubble) {
  position: absolute;
  top: 3em;
  right: 1em;
}

.basic-campsite :deep(.map-link) {
  position: absolute;
  top: 6em;
  right: 1em;
}

.basic-campsite.no-photo.with-rating :deep(.nutshells li) {
  padding-right: 4.4em;
}

footer {
  margin: auto 0 0.3em;
  position: relative;
}

.zero-availability {
  border-top: 1px solid var(--colour-page-background);
  padding: 0.35em 0 0.25em;
  font-size: var(--body);
  color: var(--colour-dark-blue);
}

.pitch-category-icons {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.zero-availability :deep(img) {
  filter: grayscale(1) brightness(1.8);
}

/* desktop & tablet */
.bookable-campsite.desktop :deep(.campsite-rating) {
  position: absolute;
  top: -0.35em;
  right: 1em;
}

.bookable-campsite.desktop :deep(.rating-bubble:before) {
  bottom: -1.3em;
  color: var(--colour-text-light);
  font-size: var(--small);
}
</style>
